import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import ViaPipelineInputs from "@via/pages/projects/runs/overview/inputs/via-pipeline-inputs.tsx";
import ExternalPipelineInputs from "@via/pages/projects/runs/overview/inputs/external-pipeline-inputs.tsx";
import Settings from "@via/pages/projects/runs/overview/settings/settings.tsx";
import {
  useParameterList,
  useRunPipelineScripts,
} from "@via/pages/projects/runs/runs.api.tsx";
import { type EnvironmentPickerFilter } from "@via/pages/projects/runs/use-launch-refusal";
import {
  getUnsatisfiedInputGroups,
  hasNoDataAttached,
  type InputNode,
  isDataRequiredByGroup,
  isPipelineExternal,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { ERROR, ROUTES, RunTabs } from "@via/utils/constants.tsx";
import { isFalsyOrEmpty } from "@via/utils/helpers.tsx";
import {
  type SaveRunInputs,
  type GetRunDetailsResponseBody,
  type RunParameterListResponseBody,
} from "common";
import {
  type FC,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

interface RunSettingsProps {
  form: UseFormReturn;
  paramsInputs: SaveRunInputs[];
  setParamsInputs: Dispatch<SetStateAction<SaveRunInputs[]>>;
  requiredGlobalInputs: InputNode[];
  setRequiredGlobalInputs: Dispatch<SetStateAction<InputNode[]>>;
  dataInputs: InputNode[];
  setDataInputs: Dispatch<SetStateAction<InputNode[]>>;
  requiredInputGroups: InputNode[][];
  setRequiredInputGroups: Dispatch<SetStateAction<InputNode[][]>>;
  displayWarnings: boolean;
  pipelineRunDetails: RunParameterListResponseBody;
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsSuccess: boolean;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  isSaveRunSuccess: boolean;
  readOnly: boolean;
  activeRunTab: RunTabs;
  /** 3G-3a, passed through to overview/settings/settings.tsx unchanged. */
  pendingRunEnvironmentId?: number | null;
  environmentPicker?: EnvironmentPickerFilter;
  onEnvironmentChange?: (
    environment: { id: number; name: string },
    source: "picker" | "user"
  ) => void;
  onEnvironmentPickerClosed?: (outcome: { picked: boolean }) => void;
}

const RunSettings: FC<RunSettingsProps> = ({
  form,
  paramsInputs,
  setParamsInputs,
  requiredGlobalInputs,
  setRequiredGlobalInputs,
  dataInputs,
  setDataInputs,
  requiredInputGroups,
  setRequiredInputGroups,
  displayWarnings,
  pipelineRunDetails,
  runSettingsData,
  isRunSettingsSuccess,
  isRunSettingsLoading,
  isRunSettingsError,
  isSaveRunSuccess,
  readOnly,
  activeRunTab,
  pendingRunEnvironmentId,
  environmentPicker,
  onEnvironmentChange,
  onEnvironmentPickerClosed,
}) => {
  const { runId = "" } = useParams();
  const { setNotification } = useNotifications();
  const {
    data: runPipelineScripts,
    isLoading: isRunPipelineScriptsLoading,
    isError: isRunPipelineScriptsError,
  } = useRunPipelineScripts(runId);

  const { isLoading: isParameterLoading } = useParameterList();

  const loading =
    isRunPipelineScriptsLoading || isRunSettingsLoading || isParameterLoading;

  const [runInputWarnings, setRunInputsWarnings] = useState<string[]>([]);

  const { control, watch } = form;
  const watchedRequiredGlobalInputs = useWatch({
    name: requiredGlobalInputs.map(({ id }) => id),
    control,
  });
  // Watched separately from the required inputs: these are @optional, so they
  // are absent from requiredGlobalInputs and nothing else re-runs the warning
  // when the user finally attaches a dataset.
  const watchedDataInputs = useWatch({
    name: dataInputs.map(({ id }) => id),
    control,
  });
  // Group members are @optional as well, and need not be data inputs, so
  // neither watch above re-runs the warning when one of them is filled in.
  const watchedRequiredInputGroups = useWatch({
    name: requiredInputGroups.flat().map(({ id }) => id),
    control,
  });

  const selectedRunEnvironment = watch("selectedRunEnvironment");
  const launchDirectory = watch("launchDirectory");
  const runEnvironmentId = watch("runEnvironmentId");

  useEffect(() => {
    if (isRunPipelineScriptsError) {
      setNotification({
        id: "unable-to-access-run",
        message: "Unable to Access Run",
        details: "Run is not found or you do not have permission to access",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [isRunPipelineScriptsError]);

  useEffect(() => {
    setRunInputsWarnings([]);
    if (
      !(
        selectedRunEnvironment?.hideWorkdir === "1" ||
        selectedRunEnvironment?.hideWorkdir === "on"
      )
    ) {
      if (!launchDirectory) {
        setRunInputsWarnings(prevWarnings => [
          ...prevWarnings,
          "Please enter an Execution Directory.",
        ]);
      } else if (!launchDirectory?.startsWith("/")) {
        setRunInputsWarnings(prevWarnings => [
          ...prevWarnings,
          "Execution directory should be full path and start with “/” symbol.",
        ]);
      }
      if (launchDirectory?.includes(" ")) {
        setRunInputsWarnings(prevWarnings => [
          ...prevWarnings,
          "There shouldn't be any space in your execution directory.",
        ]);
      }
    }

    if (isFalsyOrEmpty(runEnvironmentId) || isNaN(runEnvironmentId)) {
      setRunInputsWarnings(prevWarnings => [
        ...prevWarnings,
        "Please select a Run Environment.",
      ]);
    }
    const missingInputs = requiredGlobalInputs
      .filter(({ id }) => isFalsyOrEmpty(form.getValues(id)))
      .map(({ name }) => name);
    if (missingInputs.length) {
      setRunInputsWarnings(prevWarnings => [
        ...prevWarnings,
        `Please provide values for ${missingInputs.join(", ")}.`,
      ]);
    }
    getUnsatisfiedInputGroups(requiredInputGroups, id =>
      form.getValues(id)
    ).forEach(group => {
      setRunInputsWarnings(prevWarnings => [
        ...prevWarnings,
        `Please provide a value for one of: ${group
          .map(({ name }) => name)
          .join(", ")}.`,
      ]);
    });
    // A pipeline that accepts more than one kind of input has to mark every one
    // of them @optional, which takes them all out of requiredGlobalInputs above.
    // Nothing else notices that the run has no data attached at all, unless a
    // @required_one_of group of data inputs already blocks the launch (#443).
    if (
      !isDataRequiredByGroup(requiredInputGroups, dataInputs) &&
      hasNoDataAttached(dataInputs, id => form.getValues(id))
    ) {
      setRunInputsWarnings(prevWarnings => [
        ...prevWarnings,
        `No data is attached to ${dataInputs
          .map(({ name }) => name)
          .join(" or ")}. The run will start with no input files.`,
      ]);
    }
  }, [
    launchDirectory,
    selectedRunEnvironment,
    runEnvironmentId,
    requiredGlobalInputs,
    dataInputs,
    requiredInputGroups,
    form,
    watchedRequiredGlobalInputs,
    watchedDataInputs,
    watchedRequiredInputGroups,
  ]);

  const getRunSettingsOverviewContext = useCallback(() => {
    return {
      run: {
        name: runSettingsData?.name,
        runUUID: runSettingsData?.runUUID,
        owner: runSettingsData?.owner?.username,
        own: runSettingsData?.owner?.own,
      },
      pipeline: {
        id: runSettingsData?.mainPipeline?.id,
        name: runSettingsData?.mainPipeline?.name,
        version: runSettingsData?.mainPipeline?.version,
        pipelineType: pipelineRunDetails?.pipelineType,
      },
      project: {
        id: runSettingsData?.project?.id,
        name: runSettingsData?.project?.name,
      },
      settings: {
        runEnvironment: {
          id: runEnvironmentId,
          name: selectedRunEnvironment?.name,
          hideWorkdir: selectedRunEnvironment?.hideWorkdir,
        },
        launchDirectory: launchDirectory,
        inputs: paramsInputs.map(input => ({
          name: input.name,
          value: input.value,
          type: input.type,
          ...(input.type === "input" &&
            "serviceTokenId" in input &&
            input.serviceTokenId != null && {
              serviceTokenId: input.serviceTokenId,
            }),
        })),
        requiredInputs: requiredGlobalInputs.map(input => ({
          id: input.id,
          name: input.name,
        })),
        warnings: runInputWarnings,
      },
      readOnly: readOnly,
    };
  }, [
    runSettingsData,
    pipelineRunDetails?.pipelineType,
    runEnvironmentId,
    selectedRunEnvironment,
    launchDirectory,
    paramsInputs,
    requiredGlobalInputs,
    runInputWarnings,
    readOnly,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.RUN_DETAILS,
    contextName: "Run Settings Overview",
    getContext: getRunSettingsOverviewContext,
    systemPrompt:
      "You are analyzing run settings overview page. Help answer questions about run configuration, inputs, run environment, and pipeline settings.",
    enabled:
      !!runSettingsData &&
      isRunSettingsSuccess &&
      activeRunTab === RunTabs.RunSettings,
  });

  return (
    <>
      {!loading && displayWarnings && (
        <MessageBanner messages={runInputWarnings} type={BannerType.Warning} />
      )}
      <main className="py-4">
        <Settings
          form={form}
          runSettingsData={runSettingsData}
          isRunSettingsLoading={isRunSettingsLoading}
          isRunSettingsSuccess={isRunSettingsSuccess}
          isRunSettingsError={isRunSettingsError}
          readOnly={readOnly}
          pendingRunEnvironmentId={pendingRunEnvironmentId}
          environmentPicker={environmentPicker}
          onEnvironmentChange={onEnvironmentChange}
          onEnvironmentPickerClosed={onEnvironmentPickerClosed}
          runId={Number(runId) || null}
        />
        {loading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isRunPipelineScriptsError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : isPipelineExternal(pipelineRunDetails.pipelineType) ? (
          <ExternalPipelineInputs
            form={form}
            setRequiredGlobalInputs={setRequiredGlobalInputs}
            pipelineRunDetails={pipelineRunDetails}
            runSettingsData={runSettingsData}
            isRunSettingsLoading={isRunSettingsLoading}
            isRunSettingsSuccess={isRunSettingsSuccess}
            isRunSettingsError={isRunSettingsError}
            readOnly={readOnly}
          />
        ) : (
          <ViaPipelineInputs
            runPipelineScripts={runPipelineScripts}
            form={form}
            paramsInputs={paramsInputs}
            setParamsInputs={setParamsInputs}
            setRequiredGlobalInputs={setRequiredGlobalInputs}
            setDataInputs={setDataInputs}
            setRequiredInputGroups={setRequiredInputGroups}
            pipelineRunDetails={pipelineRunDetails}
            runSettingsData={runSettingsData}
            isRunSettingsLoading={isRunSettingsLoading}
            isRunSettingsSuccess={isRunSettingsSuccess}
            isRunSettingsError={isRunSettingsError}
            isSaveRunSuccess={isSaveRunSuccess}
            readOnly={readOnly}
          />
        )}
      </main>
    </>
  );
};

export default RunSettings;
